import {useCallback, useEffect, useRef, useState} from "react";
import styles from "./DocViewer.module.css";

// Ж5 — разглеждане на многостраничен документ (страниците идват като PNG).
// Всички страници са в един трансформиран слой, за да е един zoom за целия
// документ. Управление: колелце на мишката (около курсора), влачене, щипване с
// два пръста, двойно щракване/тапване, бутони и цял екран.
//
// Т2 — картата НЕ се тегли цяла. Землищните карти са 70–106 мегапиксела, тоест
// 266–403 MB в паметта на браузъра след декодиране; телефон отказва да ги
// покаже и страницата увисва. Затова:
//   • основата е смалена картинка (~11 MB), разпъната до пълния размер —
//     достатъчна, докато се гледа цялата карта;
//   • при приближаване отгоре ѝ се наслагват плочки от пълната резолюция, и то
//     САМО тези, които се виждат в момента.
// Координатната система на слоя си остава пълните пиксели, затова zoom-ът,
// влаченето и границите не се менят — сменя се само какво се рисува.
const MAX_SCALE = 8;
const DOUBLE_SCALE = 2;
const FALLBACK_WIDTH = 1000;
// Колко екрана около видимото да се теглят предварително — за да не се
// „проявява" плочка точно там, където човекът е спрял да влачи.
const ЗАПАС = 0.15;
// Таван на едновременно държаните плочки. 1024² плочка е 4 MB декодирана;
// 60 броя са ~250 MB в най-лошия случай, но реално видимите са под 20.
const МАКС_ПЛОЧКИ = 60;

export const DocViewer = ({pages, srcFor, tileSrcFor, geometry, label = "Приложен документ"}) => {
    const wrapRef = useRef(null);
    const boxRef = useRef(null);
    const layerRef = useRef(null);
    const pointers = useRef(new Map());
    const gesture = useRef(null);
    const fitRef = useRef(1);

    // Т2: описът на страниците идва от сървъра — размерите се знаят ПРЕДИ да е
    // заредена която и да е картинка, тоест границите на влаченето са верни от
    // първия кадър. Без опис се пада обратно към старото поведение (мери се от
    // първата заредена страница), за да не се чупи стар документ.
    const листове = (geometry && geometry.length)
        ? geometry
        : Array.from({length: pages || 0}, (_, i) => ({n: i + 1}));
    const имаОпис = Boolean(geometry && geometry.length);

    const [baseWidth, setBaseWidth] = useState(
        имаОпис ? Math.max(...geometry.map(g => g.w)) : FALLBACK_WIDTH);
    // Съотношението на първата страница се прилага на всички — така слоят има
    // вярна височина още преди отложените страници да се заредят (иначе
    // границите на влаченето биха били сгрешени).
    const [pageRatio, setPageRatio] = useState(1 / 1.414);
    const [view, setView] = useState({k: 1, x: 0, y: 0});
    const [full, setFull] = useState(false);
    // Ширината на смалената основа (мери се при зареждане). От нея се съди
    // кога основата вече не стига и трябват плочки.
    const [ширинаНаОсновата, setШиринаНаОсновата] = useState({});
    const [видимиПлочки, setВидимиПлочки] = useState([]);

    // Държи изгледа в границите на съдържанието; центрира, ако е по-малко от полето.
    const clamp = useCallback((k, x, y) => {
        const box = boxRef.current;
        const layer = layerRef.current;
        if (!box || !layer) return {k, x, y};
        const bw = box.clientWidth;
        const bh = box.clientHeight;
        const cw = baseWidth * k;
        const ch = layer.offsetHeight * k;
        const nx = cw <= bw ? (bw - cw) / 2 : Math.min(0, Math.max(bw - cw, x));
        const ny = ch <= bh ? (bh - ch) / 2 : Math.min(0, Math.max(bh - ch, y));
        return {k, x: nx, y: ny};
    }, [baseWidth]);

    const fitToWidth = useCallback(() => {
        const box = boxRef.current;
        if (!box) return;
        const k = box.clientWidth / baseWidth;
        fitRef.current = k;
        setView(clamp(k, 0, 0));
    }, [baseWidth, clamp]);

    const zoomAt = useCallback((factor, px, py) => {
        setView(v => {
            const k = Math.min(MAX_SCALE, Math.max(fitRef.current, v.k * factor));
            const ratio = k / v.k;
            return clamp(k, px - (px - v.x) * ratio, py - (py - v.y) * ratio);
        });
    }, [clamp]);

    const zoomCenter = (factor) => {
        const box = boxRef.current;
        if (!box) return;
        zoomAt(factor, box.clientWidth / 2, box.clientHeight / 2);
    };

    // Ширината идва от първата заредена страница — тогава k = 1 значи „истински
    // пиксели" на растера и приближаването остига до пълната резолюция.
    const onFirstPageLoad = (e) => {
        const {naturalWidth: w, naturalHeight: h} = e.target;
        if (w) setBaseWidth(w);
        if (w && h) setPageRatio(w / h);
    };

    useEffect(() => {
        fitToWidth();
    }, [fitToWidth, pages]);

    // Т2 — кои плочки са видими сега. Смята се в пълни пиксели (координатната
    // система на слоя), после се превежда в редове и колони.
    useEffect(() => {
        if (!имаОпис || !tileSrcFor) { setВидимиПлочки([]); return; }
        const box = boxRef.current;
        if (!box) return;
        const bw = box.clientWidth, bh = box.clientHeight;

        const списък = [];
        let отгоре = 0;   // къде започва страницата по вертикала, в пълни пиксели
        for (const g of листове) {
            const овШирина = ширинаНаОсновата[g.n];
            // Плочки трябват едва когато основата вече се разтяга над своята
            // резолюция. Докато се гледа цялата карта — смалената е достатъчна.
            const трябват = овШирина ? view.k > (овШирина / g.w) * 1.02 : false;
            if (трябват) {
                // видимото поле в координатите на слоя
                const x0 = (-view.x) / view.k - bw * ЗАПАС / view.k;
                const x1 = (-view.x + bw) / view.k + bw * ЗАПАС / view.k;
                const y0 = (-view.y) / view.k - отгоре - bh * ЗАПАС / view.k;
                const y1 = (-view.y + bh) / view.k - отгоре + bh * ЗАПАС / view.k;

                const c0 = Math.max(0, Math.floor(x0 / g.tile));
                const c1 = Math.min(g.cols - 1, Math.floor(x1 / g.tile));
                const r0 = Math.max(0, Math.floor(y0 / g.tile));
                const r1 = Math.min(g.rows - 1, Math.floor(y1 / g.tile));
                for (let r = r0; r <= r1; r++) {
                    for (let c = c0; c <= c1; c++) {
                        списък.push({
                            key: `${g.n}-${c}-${r}`, n: g.n, c, r,
                            x: c * g.tile, y: r * g.tile,
                            w: Math.min(g.tile, g.w - c * g.tile),
                            h: Math.min(g.tile, g.h - r * g.tile),
                        });
                    }
                }
            }
            отгоре += g.h + 8;   // 8 px разстояние между листовете (виж CSS)
        }
        // При много силно отдалечаване и голяма карта списъкът може да
        // подскочи; таванът пази паметта, а видимите почти никога не са толкова.
        setВидимиПлочки(списък.slice(0, МАКС_ПЛОЧКИ));
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [view, имаОпис, tileSrcFor, ширинаНаОсновата, geometry]);

    useEffect(() => {
        const box = boxRef.current;
        if (!box || typeof ResizeObserver === "undefined") return;
        const ro = new ResizeObserver(() => fitToWidth());
        ro.observe(box);
        return () => ro.disconnect();
    }, [fitToWidth]);

    // Колелцето трябва да е непасивно, за да може да спре скролването на страницата.
    useEffect(() => {
        const box = boxRef.current;
        if (!box) return;
        const onWheel = (e) => {
            e.preventDefault();
            const rect = box.getBoundingClientRect();
            zoomAt(Math.exp(-e.deltaY * 0.0015), e.clientX - rect.left, e.clientY - rect.top);
        };
        box.addEventListener("wheel", onWheel, {passive: false});
        return () => box.removeEventListener("wheel", onWheel);
    }, [zoomAt]);

    useEffect(() => {
        const onChange = () => setFull(Boolean(document.fullscreenElement || document.webkitFullscreenElement));
        document.addEventListener("fullscreenchange", onChange);
        document.addEventListener("webkitfullscreenchange", onChange);
        return () => {
            document.removeEventListener("fullscreenchange", onChange);
            document.removeEventListener("webkitfullscreenchange", onChange);
        };
    }, []);

    const toggleFullscreen = () => {
        const el = wrapRef.current;
        if (!el) return;
        if (document.fullscreenElement || document.webkitFullscreenElement) {
            (document.exitFullscreen || document.webkitExitFullscreen)?.call(document);
        } else {
            (el.requestFullscreen || el.webkitRequestFullscreen)?.call(el);
        }
    };

    // --- жестове: един показалец влачи, два щипят ---
    const localPoint = (e) => {
        const rect = boxRef.current.getBoundingClientRect();
        return {x: e.clientX - rect.left, y: e.clientY - rect.top};
    };

    const onPointerDown = (e) => {
        boxRef.current.setPointerCapture?.(e.pointerId);
        pointers.current.set(e.pointerId, localPoint(e));
        const pts = [...pointers.current.values()];
        if (pts.length === 1) {
            gesture.current = {type: "pan", start: pts[0], origin: view};
        } else if (pts.length === 2) {
            const dist = Math.hypot(pts[0].x - pts[1].x, pts[0].y - pts[1].y);
            gesture.current = {type: "pinch", dist, origin: view};
        }
    };

    const onPointerMove = (e) => {
        if (!pointers.current.has(e.pointerId)) return;
        pointers.current.set(e.pointerId, localPoint(e));
        const pts = [...pointers.current.values()];
        const g = gesture.current;
        if (!g) return;

        if (g.type === "pan" && pts.length === 1) {
            const dx = pts[0].x - g.start.x;
            const dy = pts[0].y - g.start.y;
            setView(clamp(g.origin.k, g.origin.x + dx, g.origin.y + dy));
        } else if (g.type === "pinch" && pts.length === 2) {
            const dist = Math.hypot(pts[0].x - pts[1].x, pts[0].y - pts[1].y);
            if (!g.dist) return;
            const mid = {x: (pts[0].x + pts[1].x) / 2, y: (pts[0].y + pts[1].y) / 2};
            const k = Math.min(MAX_SCALE, Math.max(fitRef.current, g.origin.k * (dist / g.dist)));
            const ratio = k / g.origin.k;
            setView(clamp(k, mid.x - (mid.x - g.origin.x) * ratio, mid.y - (mid.y - g.origin.y) * ratio));
        }
    };

    const onPointerUp = (e) => {
        pointers.current.delete(e.pointerId);
        if (pointers.current.size === 0) gesture.current = null;
        else {
            // след вдигане на единия пръст продължаваме като влачене
            const pts = [...pointers.current.values()];
            gesture.current = {type: "pan", start: pts[0], origin: view};
        }
    };

    const onDoubleClick = (e) => {
        if (view.k > fitRef.current * 1.05) {
            fitToWidth();
            return;
        }
        const p = localPoint(e);
        zoomAt((fitRef.current * DOUBLE_SCALE) / view.k, p.x, p.y);
    };

    if (!pages) return null;

    return (
        <div className={styles.wrap} ref={wrapRef}>
            <div className={styles.bar}>
                <span className="fw-bold" style={{color: "var(--app-gold)"}}>{label}</span>
                <div className="btn-group btn-group-sm" role="group">
                    <button type="button" className="btn btn-outline-secondary"
                            onClick={() => zoomCenter(1 / 1.3)} aria-label="Отдалечи">−
                    </button>
                    <button type="button" className="btn btn-outline-secondary"
                            onClick={fitToWidth}>по ширина
                    </button>
                    <button type="button" className="btn btn-outline-secondary"
                            onClick={() => zoomCenter(1.3)} aria-label="Приближи">+
                    </button>
                </div>
                <button type="button" className="btn btn-outline-secondary btn-sm"
                        onClick={toggleFullscreen}>
                    {full ? "Изход от цял екран" : "Цял екран"}
                </button>
                {/* Р6: подсказката е разделена на ДВЕ — бутоните горе работят само
                    с мишка, а на телефон се борави с пръсти. Слято в едно
                    изречение човекът не разбира кое за него важи. */}
                <span className={styles.hint}>
                    {Math.round((view.k / (fitRef.current || 1)) * 100)}%
                    <span className={styles.hintLine}>
                        <strong>На компютър:</strong> бутоните по-горе, колелце за приближаване,
                        влачене за местене, двойно щракване за превключване.
                    </span>
                    <span className={styles.hintLine}>
                        <strong>На телефон и таблет:</strong> два пръста за приближаване и местене,
                        двойно тапване за превключване.
                    </span>
                </span>
            </div>
            <div className={styles.box} ref={boxRef}
                 onPointerDown={onPointerDown}
                 onPointerMove={onPointerMove}
                 onPointerUp={onPointerUp}
                 onPointerCancel={onPointerUp}
                 onDoubleClick={onDoubleClick}>
                <div className={styles.layer} ref={layerRef}
                     style={{
                         width: `${baseWidth}px`,
                         transform: `translate(${view.x}px, ${view.y}px) scale(${view.k})`,
                     }}>
                    {!имаОпис && Array.from({length: pages}, (_, i) => i + 1).map(n => (
                        <img key={n} className={styles.page} alt={`Страница ${n}`}
                             src={srcFor(n)} draggable={false}
                             style={{aspectRatio: pageRatio}}
                             loading={n === 1 ? "eager" : "lazy"}
                             onLoad={n === 1 ? onFirstPageLoad : undefined}/>
                    ))}

                    {/* Т2: смалената основа + плочките от пълната резолюция. */}
                    {имаОпис && листове.map(g => (
                        <div key={g.n} className={styles.sheet}
                             style={{width: `${g.w}px`, height: `${g.h}px`}}>
                            <img className={styles.base} alt={`Страница ${g.n}`}
                                 src={srcFor(g.n)} draggable={false}
                                 onLoad={(e) => setШиринаНаОсновата(с => (
                                     с[g.n] === e.target.naturalWidth
                                         ? с
                                         : {...с, [g.n]: e.target.naturalWidth}))}/>
                            {видимиПлочки.filter(t => t.n === g.n).map(t => (
                                <img key={t.key} className={styles.tile}
                                     alt="" aria-hidden="true" draggable={false}
                                     src={tileSrcFor(t.n, t.c, t.r)}
                                     style={{
                                         left: `${t.x}px`, top: `${t.y}px`,
                                         width: `${t.w}px`, height: `${t.h}px`,
                                     }}/>
                            ))}
                        </div>
                    ))}
                </div>
            </div>
        </div>
    );
};
